/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react';
import classNames from 'classnames';
import { Table, TableProps, Modal } from 'react-bootstrap';
import FeatherIcon from 'feather-icons-react';
import { useTranslation } from 'react-i18next';

import { useAppContext } from 'providers/AppProvider';
import Button from './Button';
import UpdatePaymentInstallmentReport from 'pages/reports/installment-reports/UpdatePaymentInstallmentReport';

interface NestedRow {
  name: string;
  phone?: string;
  item?: string;
  total?: number;
  paid?: number;
  balance?: number;
  status?: string;
  children?: NestedRow[];
  [key: string]: any;
}

interface InstallmentAdvanceTableProps<T> {
  headerClassName?: string;
  bodyClassName?: string;
  rowClassName?: string;
  tableProps?: TableProps;
  hasFooter?: boolean;
  data: T[];
  columns: any;
  refetch?: any;
  setUpdatePaymentBox: any;
}

const InstallmentAdvanceTable = <T extends NestedRow>({
  headerClassName,
  bodyClassName,
  rowClassName,
  tableProps,
  hasFooter,
  data,
  columns,
  refetch,
  setUpdatePaymentBox
}: InstallmentAdvanceTableProps<T>) => {
  const { t } = useTranslation();

  const [expandedRows, setExpandedRows] = React.useState<
    Record<string, boolean>
  >({});
  const [showModal, setShowModal] = React.useState<boolean>(false);
  const [selectedPid, setSelectedPid] = React.useState<number | null>(null);
  const [selectedObjectItem, setSelectedObjectItem] = useState<any>({});

  const {
    config: { theme }
  } = useAppContext();

  const toggleRowExpansion = (rowId: string) => {
    setExpandedRows(prev => ({ ...prev, [rowId]: !prev[rowId] }));
  };

  const handleAccept = (pid: number, item: any) => {
    setSelectedObjectItem(item);
    setSelectedPid(pid);
    setShowModal(true); // Show modal before proceeding
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedPid(null);
    setSelectedObjectItem({});
  };

  const renderRows = (rows: T[], level: number = 0) => {
    return rows.map((row: any, index) => {
      const rowId = `${row.name}-${index}`;
      const isExpanded = expandedRows[rowId];

      return (
        <React.Fragment key={rowId}>
          <tr
            className={classNames(rowClassName, { [`level-${level}`]: true })}
          >
            <td style={{ paddingLeft: `${level * 20}px` }}>
              {row.children && row.children.length > 0 ? (
                <span
                  onClick={() => toggleRowExpansion(rowId)}
                  className="icons-report"
                >
                  {isExpanded ? (
                    <FeatherIcon icon="chevron-up" size={20} />
                  ) : (
                    <FeatherIcon icon="chevron-down" size={20} />
                  )}
                </span>
              ) : null}
              {row.name || '\u00A0'}
            </td>
            {columns.slice(1).map((column: any) =>
              /* prettier-ignore */
              <td key={column.accessorKey}>
                {typeof column.cell === 'function'
                  ? column.cell({
                      row: {
                        original: row
                      }
                    })
                    : row[column.accessorKey] !== undefined
                    ? row[column.accessorKey]
                    : ''}
              </td>
            )}
          </tr>
          {isExpanded && row.children && (
            <tr className="child-row-boxes">
              <td
                colSpan={columns.length}
                style={{ paddingLeft: `${(level + 1) * 20}px` }}
              >
                <h4
                  className="payment-schedule"
                  style={{
                    color: theme === 'dark' ? '#fff' : '#000'
                  }}
                >
                  {t('payment_schedule')}
                </h4>
                <div className="child-boxes-container">
                  {row.children.map((child: any, childIndex: any) =>
                    /* prettier-ignore */
                    <div
                      key={`${rowId}-child-${childIndex}`}
                      className={`child-box ${child.status === 'EXPIRED' ? 'expired-animate' : ''}`}
                      style={{
                        backgroundColor:
                          child.status === 'EXPIRED'
                            ? 'rgba(239, 68, 68, 0.2)'
                            : child.status === 'PAID'
                              ? 'rgba(16, 185, 129, 0.2)'
                              : child.status === 'WAITING'
                                ? 'rgba(245, 158, 11, 0.2)'
                                : child.status === 'COMING'
                                  ? '#374151'
                                  : child.status === 'PARTIALLY_PAID'
                                    ? '#fed7aa'
                                  : '',
                        borderColor:
                          child.status === 'EXPIRED'
                            ? '#ef4444'
                            : child.status === 'PAID'
                              ? '#10b981'
                              : child.status === 'WAITING'
                                ? '#f59e0b'
                                : child.status === 'COMING'
                                  ? '#4b5563'
                                  : child.status === 'PARTIALLY_PAID'
                                    ? '#9a3412'
                                  : ''
                      }}
                    >
                      <span
                        className="child-title"
                        style={{
                          color:
                            theme === 'dark'
                              ? '#9ca3af'
                              : child.status === 'COMING'
                                ? '#fff'
                                : '#000'
                        }}
                      >
                        {new Intl.DateTimeFormat('ru-RU', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric'
                        }).format(new Date(child.name)) || ''}
                      </span>
                      <span
                        style={{
                          color:
                            theme === 'dark'
                              ? '#fff'
                              : child.status === 'COMING'
                                ? '#fff'
                                : '#000'
                        }}
                      >
                        {child.price !== undefined
                          ? new Intl.NumberFormat('en-US', {
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 4,
                              useGrouping: true
                            })
                              .format(child.price)
                              .replace(/,/g, ' ')
                          : 0}
                      </span>
                      <span
                        style={{
                          color:
                            child.status === 'EXPIRED'
                              ? '#ef4444'
                              : child.status === 'PAID'
                                ? '#10b981'
                                : child.status === 'WAITING'
                                  ? '#f59e0b'
                                  : child.status === 'COMING'
                                    ? '#fff'
                                    : child.status === 'PARTIALLY_PAID'
                                    ? '#9a3412'
                                    : ''
                        }}
                      >
                        {child.status !== undefined
                          ? child.status === 'EXPIRED'
                            ? t('overdue')
                            : child.status === 'PAID'
                              ? t('paid')
                              : child.status === 'WAITING'
                                ? t('remaining')
                                : child.status === 'COMING'
                                  ? t('coming')
                                  : child.status === 'PARTIALLY_PAID'
                                  ? t('partially_paid')
                                  : ''
                          : ''}
                      </span>

                      {child.can_pay === true && (
                        <Button
                        variant='primary'
                          style={{
                            padding: '4px 8px',
                            whiteSpace: 'nowrap', 
                            marginTop: "-50px",
                            marginRight: "-5px",
                            fontSize: "12px",
                            alignSelf: 'flex-end' 
                          }}
                          onClick={() => handleAccept(child?.children_pid,child)}
                        >
                          $
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </td>
            </tr>
          )}
        </React.Fragment>
      );
    });
  };

  return (
    <div className="scrollbar ms-n1 ps-1">
      <Table {...tableProps}>
        <thead className={headerClassName}>
          <tr>
            {columns.map((header: any) => (
              <th
                key={header.accessorKey}
                {...header.meta?.headerProps}
                className={header.meta?.headerProps?.className}
              >
                {header.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className={bodyClassName}>{renderRows(data)}</tbody>
        {hasFooter && <tfoot>{/* Footer logic if needed */}</tfoot>}
      </Table>

      <Modal
        show={showModal}
        onHide={handleCloseModal}
        size="sm"
        centered
        contentClassName="border border-300"
      >
        <Modal.Header closeButton>
          <Modal.Title>{t('confirm_payment')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <UpdatePaymentInstallmentReport
            selectedPid={selectedPid}
            setShowModal={setShowModal}
            refetch={refetch}
            setSelectedPid={setSelectedPid}
            selectedObjectItem={selectedObjectItem}
            setSelectedObjectItem={setSelectedObjectItem}
            setUpdatePaymentBox={setUpdatePaymentBox}
          />
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default InstallmentAdvanceTable;
